import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Link as ChakraLink,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useSetRecoilState } from 'recoil';
import FalconIcon from '../components/common/FalconIcon';
import FalconDataScene from '../components/common/FalconDataScene';
import { userAtom } from '../state/userAtom';
import { sitesListAtom } from '../state/sitesAtom';
import { authApi, sitesApi } from '../api';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const setUser = useSetRecoilState(userAtom);
  const setSites = useSetRecoilState(sitesListAtom);
  const navigate = useNavigate();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Username or email is required';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await authApi.login(email, password);
      setUser(response.user);

      // Fetch sites after login
      try {
        const sitesResponse = await sitesApi.getAll();
        setSites(sitesResponse.data || []);
      } catch (sitesErr) {
        console.error('Failed to fetch sites:', sitesErr);
      }

      toast({
        title: 'Welcome back!',
        status: 'success',
        duration: 3000,
      });
      navigate('/dashboard');
    } catch (err) {
      toast({
        title: 'Login failed',
        description: err.message || 'Invalid email or password',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={12}
      bg={bgColor}
      position="relative"
      overflow="hidden"
    >
      {/* Top Left Logo */}
      <ChakraLink
        as={Link}
        to="/"
        position="absolute"
        top={6}
        left={6}
        zIndex={10}
        display="flex"
        alignItems="center"
        gap={2}
        _hover={{ opacity: 0.8 }}
      >
        <Box as="img" src="/falcon.svg" h="36px" />
        <Text fontFamily="logo" fontWeight="800" fontSize="lg" bgGradient="linear(to-r, brand.500, accent.500)" bgClip="text">StatFalcon</Text>
      </ChakraLink>

      <FalconDataScene />

      <Box
        w="100%"
        maxW="400px"
        bg={cardBg}
        borderRadius="2xl"
        border="1px solid"
        borderColor={borderColor}
        p={8}
        shadow="xl"
        position="relative"
        zIndex={1}
        backdropFilter="blur(10px)"
      >
        <VStack spacing={6} align="stretch">
          {/* Logo & Header */}
          <VStack spacing={2} textAlign="center">
            <Box
              p={3}
              bgGradient="linear(135deg, brand.500 0%, accent.500 100%)"
              borderRadius="xl"
              color="white"
              mb={2}
            >
              <FalconIcon boxSize={8} />
            </Box>
            <Heading
              size="2xl"
              fontFamily="logo"
              fontWeight="700"
              letterSpacing="-0.01em"
              bgGradient="linear(to-r, brand.500, accent.500)"
              bgClip="text"
            >
              StatFalcon
            </Heading>
            <Text color="gray.500">Sign in to your account</Text>
          </VStack>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Username or Email</FormLabel>
                <Input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin or you@example.com"
                  borderRadius="xl"
                  size="lg"
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel>Password</FormLabel>
                <InputGroup size="lg">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    borderRadius="xl"
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={<FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                w="100%"
                borderRadius="xl"
                isLoading={isLoading}
                loadingText="Signing in..."
              >
                Sign In
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>

      <Text
        position="absolute"
        bottom={4}
        left="50%"
        transform="translateX(-50%)"
        fontSize="xs"
        color="gray.500"
      >
        © {new Date().getFullYear()} StatFalcon. All rights reserved.
      </Text>
    </Box>
  );
}

export default LoginPage;
